import React, { useEffect, useState } from "react";
import {
  Card,
  styled,
  CardContent,
  Typography,
  CardMedia,
  Grid,
  Box,
  Container,
  useTheme,
  alpha,
  Fade,
  Grow,
  Chip,
} from "@mui/material";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import EmailIcon from "@mui/icons-material/Email";
import BusinessIcon from "@mui/icons-material/Business";
import StarIcon from "@mui/icons-material/Star";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

// Import images
import smithaPhoto from "assets/smitha.png";
import smita_naik from "assets/smita_naik.jpeg";

// Styled components
const TeamSection = styled(Box)(({ theme }) => ({
  position: "relative",
  padding: theme.spacing(10, 0),
  background: "linear-gradient(135deg, #ffffff 0%, #f5f7ff 100%)",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: "linear-gradient(90deg, transparent, #667eea, transparent)",
  },
}));

const TeamCard = styled(Card)(({ theme }) => ({
  maxWidth: "380px",
  margin: "0 auto",
  background: "white",
  borderRadius: theme.shape.borderRadius * 3,
  overflow: "hidden",
  boxShadow: theme.shadows[4],
  transition: "all 0.4s ease",
  position: "relative",
  "&:hover": {
    transform: "translateY(-16px)",
    boxShadow: theme.shadows[16],
    "& .team-image": {
      transform: "scale(1.05)",
    },
    "& .social-overlay": {
      opacity: 1,
      transform: "translateY(0)",
    },
  },
}));

const TeamCardMedia = styled(CardMedia)(({ theme }) => ({
  height: "320px",
  position: "relative",
  overflow: "hidden",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "100px",
    background: "linear-gradient(to top, rgba(0,0,0,0.3), transparent)",
  },
}));

const SocialOverlay = styled(Box)(({ theme }) => ({
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  padding: theme.spacing(2),
  background: "linear-gradient(to top, rgba(0,0,0,0.8), transparent)",
  display: "flex",
  justifyContent: "center",
  gap: theme.spacing(2),
  opacity: 0,
  transform: "translateY(20px)",
  transition: "all 0.3s ease",
  zIndex: 2,
}));

const SocialIcon = styled(Box)(({ theme }) => ({
  width: "40px",
  height: "40px",
  borderRadius: "50%",
  background: "white",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#667eea",
  cursor: "pointer",
  transition: "all 0.3s ease",
  "&:hover": {
    background: "#667eea",
    color: "white",
    transform: "translateY(-2px)",
  },
}));

const ExperienceChip = styled(Chip)(({ theme }) => ({
  position: "absolute",
  top: "16px",
  right: "16px",
  background: "linear-gradient(135deg, #667eea, #764ba2)",
  color: "white",
  fontWeight: 600,
  zIndex: 2,
  boxShadow: theme.shadows[3],
}));

const TeamContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(3),
  background: "white",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: "-2px",
    left: "50%",
    transform: "translateX(-50%)",
    width: "80px",
    height: "3px",
    background: "linear-gradient(90deg, #667eea, #764ba2)",
    borderRadius: "2px",
  },
}));

const TeamName = styled(Typography)(({ theme }) => ({
  color: "#2c3e50",
  fontSize: "1.5rem",
  fontWeight: 700,
  marginBottom: theme.spacing(0.5),
  fontFamily: "'Poppins', sans-serif",
}));

const TeamPosition = styled(Typography)(({ theme }) => ({
  color: "#667eea",
  fontSize: "1rem",
  fontWeight: 600,
  marginBottom: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

const TeamDescription = styled(Typography)(({ theme }) => ({
  color: "#666",
  fontSize: "0.95rem",
  lineHeight: 1.6,
  marginBottom: theme.spacing(2),
}));

const SkillsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
  marginTop: theme.spacing(2),
}));

const SkillTag = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0.5, 1.5),
  background: alpha("#667eea", 0.1),
  color: "#667eea",
  borderRadius: "20px",
  fontSize: "0.75rem",
  fontWeight: 500,
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  textAlign: "center",
  marginBottom: theme.spacing(6),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "3rem",
  fontWeight: 800,
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  marginBottom: theme.spacing(2),
  fontFamily: "'Poppins', sans-serif",
  [theme.breakpoints.down("md")]: {
    fontSize: "2.5rem",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "2rem",
  },
}));

const SectionSubtitle = styled(Typography)(({ theme }) => ({
  color: "#666",
  fontSize: "1.1rem",
  maxWidth: "600px",
  margin: "0 auto",
}));

const Underline = styled(Box)(({ theme }) => ({
  height: "4px",
  width: "80px",
  background: "linear-gradient(90deg, #667eea, #764ba2)",
  margin: `${theme.spacing(2)} auto`,
  borderRadius: "2px",
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    width: "20px",
    height: "20px",
    background: "#667eea",
    borderRadius: "50%",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    animation: "pulse 2s ease-in-out infinite",
  },
  "@keyframes pulse": {
    "0%, 100%": { transform: "translate(-50%, -50%) scale(1)" },
    "50%": { transform: "translate(-50%, -50%) scale(1.2)" },
  },
}));

const teamMembers = [
  {
    name: "Smitha Goyal",
    position: "CEO & Founder",
    description:
      "California Bay Area resident with 15+ years of experience in Customer Service & Success. Passionate about revolutionizing elderly care through technology and compassion.",
    photo: smithaPhoto,
    experience: "15+ Years",
    skills: [
      "Customer Success",
      "Strategic Planning",
      "Business Development",
      "Team Leadership",
    ],
    linkedin: "#",
    email: "smitha@athomegericare.com",
  },
  {
    name: "Smita Naik",
    position: "VP – Business Development",
    description:
      "Extensive experience working in elderly care services with a deep understanding of healthcare operations and client relationships.",
    photo: smita_naik,
    experience: "12+ Years",
    skills: [
      "Healthcare Operations",
      "Client Relations",
      "Market Expansion",
      "Partnership Building",
    ],
    linkedin: "#",
    email: "smita@athomegericare.com",
  },
];

const MeetTheTeam = ({ teamsRef }) => {
  const theme = useTheme();
  const [visibleCards, setVisibleCards] = useState([]);

  useEffect(() => {
    const observers = [];
    const cards = document.querySelectorAll(".team-card");

    cards.forEach((card, index) => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisibleCards((prev) => [...new Set([...prev, index])]);
          }
        },
        { threshold: 0.2 },
      );
      observer.observe(card);
      observers.push(observer);
    });

    return () => observers.forEach((obs) => obs.disconnect());
  }, []);

  return (
    <TeamSection ref={teamsRef}>
      {/* Background Elements */}
      <Box
        sx={{
          position: "absolute",
          top: "10%",
          right: "5%",
          width: "300px",
          height: "300px",
          borderRadius: "50%",
          background:
            "linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.05))",
          filter: "blur(60px)",
          zIndex: 0,
          animation: "float 8s ease-in-out infinite",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "10%",
          left: "5%",
          width: "250px",
          height: "250px",
          borderRadius: "50%",
          background:
            "linear-gradient(135deg, rgba(118, 75, 162, 0.08), rgba(102, 126, 234, 0.03))",
          filter: "blur(40px)",
          zIndex: 0,
          animation: "float 10s ease-in-out infinite reverse",
        }}
      />

      <Container maxWidth="lg">
        <SectionHeader>
          <Fade in timeout={800}>
            <SectionTitle variant="h2">Meet Our Leadership</SectionTitle>
          </Fade>
          <Underline />
          <Fade in timeout={1000}>
            <SectionSubtitle>
              Dedicated professionals combining expertise with compassion to
              deliver exceptional care
            </SectionSubtitle>
          </Fade>
        </SectionHeader>

        <Grid container spacing={4} justifyContent="center">
          {teamMembers.map((member, index) => (
            <Grid
              item
              key={index}
              xs={12}
              sm={6}
              lg={5}
              sx={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Grow
                in={visibleCards.includes(index)}
                timeout={500 + index * 200}
              >
                <TeamCard className="team-card">
                  {/* Experience Badge */}
                  <ExperienceChip
                    icon={<StarIcon sx={{ fontSize: "1rem" }} />}
                    label={member.experience}
                  />

                  {/* Team Member Image */}
                  <TeamCardMedia
                    className="team-image"
                    image={member.photo}
                    title={member.name}
                    sx={{
                      height: { xs: "350px", sm: "400px", lg: "350px" },
                      transition: "transform 0.5s ease",
                    }}
                  />

                  {/* Social Links Overlay */}
                  <SocialOverlay className="social-overlay">
                    {/* <SocialIcon
                      onClick={() => window.open(member.linkedin, "_blank")}
                    >
                      <LinkedInIcon />
                    </SocialIcon> */}
                    <SocialIcon
                      onClick={() =>
                        (window.location.href = `mailto:${member.email}`)
                      }
                    >
                      <EmailIcon />
                    </SocialIcon>
                  </SocialOverlay>

                  {/* Team Member Info */}
                  <TeamContent>
                    <TeamName variant="h5">{member.name}</TeamName>
                    <TeamPosition variant="body1">
                      <BusinessIcon fontSize="small" />
                      {member.position}
                    </TeamPosition>
                    <TeamDescription variant="body2">
                      {member.description}
                    </TeamDescription>

                    {/* Skills Tags */}
                    <SkillsContainer>
                      {member.skills.map((skill, skillIndex) => (
                        <Fade
                          in={visibleCards.includes(index)}
                          timeout={700 + skillIndex * 100}
                          key={skill}
                        >
                          <SkillTag>{skill}</SkillTag>
                        </Fade>
                      ))}
                    </SkillsContainer>

                    {/* View Profile Button */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mt: 2,
                        color: "#667eea",
                        fontWeight: 600,
                        fontSize: "0.9rem",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          gap: 2,
                          color: "#764ba2",
                        },
                      }}
                    >
                      <span>View Full Profile</span>
                      <ArrowForwardIcon fontSize="small" />
                    </Box>
                  </TeamContent>
                </TeamCard>
              </Grow>
            </Grid>
          ))}
        </Grid>

        {/* Team Philosophy */}
        <Fade in timeout={1500}>
          <Box
            sx={{
              mt: 8,
              p: 4,
              background:
                "linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)",
              borderRadius: theme.shape.borderRadius * 3,
              textAlign: "center",
              border: `1px solid ${alpha("#667eea", 0.2)}`,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: theme.palette.primary.main,
                mb: 2,
                fontFamily: "'Poppins', sans-serif",
              }}
            >
              Our Leadership Philosophy
            </Typography>
            <Typography
              sx={{
                color: "text.secondary",
                maxWidth: "800px",
                margin: "0 auto",
                fontSize: "1.1rem",
                lineHeight: 1.8,
              }}
            >
              At AtHomeGeriCare, our leadership team combines professional
              expertise with personal passion for elderly care. We believe in
              leading with empathy, innovation, and a commitment to excellence
              in every aspect of our service.
            </Typography>
          </Box>
        </Fade>
      </Container>
    </TeamSection>
  );
};

export default MeetTheTeam;
